
import { makeAIApiCall } from "@/components/dashboard/candidates/ai-api-service";
import { supabase } from "@/integrations/supabase/client";

export interface SentimentResults {
  overall: number;
  categories: {
    positive: number;
    neutral: number;
    negative: number;
  };
  insights: {
    positiveTrends: string;
    areasForImprovement: string;
    platformFocus: string;
  };
  keywords: Array<{
    word: string;
    count: number;
    sentiment: number;
  }>;
  platforms: Array<{
    name: string;
    sentiment: number;
    volume: number;
  }>;
  timeline: Array<{
    period: string;
    sentiment: number;
  }>;
}

interface SearchResult {
  title: string;
  snippet: string;
  link: string;
  displayLink: string;
}

const performWebSearch = async (query: string): Promise<SearchResult[]> => {
  try {
    console.log('Performing web search for:', query);
    
    const { data, error } = await supabase.functions.invoke('web-search-service', {
      body: { 
        query: query,
        country: 'United States',
        searchType: 'General'
      }
    });

    if (error) {
      console.error('Web search error:', error);
      return [];
    }

    return data?.searchResults || [];
  } catch (error) {
    console.error('Error in web search:', error);
    return [];
  }
};

const buildSearchQueries = (companyName: string, selectedQuestion: string): string[] => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
  
  const baseQueries = [
    `"${companyName}" sentiment news ${currentMonth} ${currentYear}`,
    `"${companyName}" latest news today recent ${currentYear}`,
    `"${companyName}" customer reviews feedback ${currentYear}`,
    `"${companyName}" LinkedIn mentions opinions ${currentYear}`,
    `"${companyName}" Twitter X sentiment ${currentMonth} ${currentYear}`,
    `"${companyName}" Facebook sentiment recent ${currentYear}`,
    `"${companyName}" Reddit discussions ${currentMonth} ${currentYear}`,
    `"${companyName}" Instagram mentions ${currentYear}`,
    `"${companyName}" breaking news recent events ${currentYear}`,
  ];

  // Add specific queries based on the analysis topic
  if (selectedQuestion.includes('product quality')) {
    baseQueries.push(`"${companyName}" product quality reviews complaints ${currentYear}`);
  } else if (selectedQuestion.includes('customer service')) {
    baseQueries.push(`"${companyName}" customer service support experience ${currentYear}`);
  } else if (selectedQuestion.includes('recent announcements')) {
    baseQueries.push(`"${companyName}" news announcements press release ${currentMonth} ${currentYear}`);
  } else if (selectedQuestion.includes('competitors')) {
    baseQueries.push(`"${companyName}" vs competitors comparison ${currentYear}`);
  } else if (selectedQuestion.includes('features')) {
    baseQueries.push(`"${companyName}" features improvement requests ${currentYear}`);
  }

  return baseQueries;
};

const consolidateSearchResults = (allResults: SearchResult[]): string => {
  if (allResults.length === 0) {
    return "No recent web search results found for this company.";
  }

  const currentDate = new Date().toISOString().split('T')[0];
  const consolidatedContent = allResults
    .slice(0, 15)
    .map((result, index) => {
      return `[${index + 1}] RECENT SEARCH RESULT (as of ${currentDate}):\nTitle: ${result.title}\nSource: ${result.displayLink}\nContent: ${result.snippet}\n`;
    })
    .join('\n');

  return consolidatedContent;
};

export const analyzeSentiment = async (
  companyName: string,
  selectedQuestion: string,
  aiEngine: string
): Promise<SentimentResults> => {
  try {
    console.log('Starting real-time sentiment analysis for:', companyName);
    
    // Use the dedicated analyze-sentiment edge function
    // which uses Perplexity for live web search + gpt-5.4-nano for structuring
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { data, error } = await supabase.functions.invoke('analyze-sentiment', {
      body: { companyName, selectedQuestion, userTimezone }
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(error.message);
    }

    if (!data || !data.overall && data.overall !== 0) {
      console.error('Invalid response from edge function:', data);
      throw new Error('Invalid sentiment data received');
    }

    console.log('Successfully received real-time sentiment data');
    return data as SentimentResults;
  } catch (error) {
    console.error("Error in real-time sentiment analysis:", error);
    console.log("Falling back to standard sentiment analysis");
    return analyzeSentimentFallback(companyName, selectedQuestion, aiEngine);
  }
};

const analyzeSentimentFallback = async (
  companyName: string,
  selectedQuestion: string,
  aiEngine: string
): Promise<SentimentResults> => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.toLocaleString('default', { month: 'short' });
  
  const messages = [{
    role: "system",
    content: `You are a sentiment analysis AI that analyzes current public sentiment about companies. Today's date is ${currentDate.toDateString()}. Provide analysis that reflects recent sentiment trends up to the current month (${currentMonth} ${currentYear}). Return JSON with this structure: {"overall": number between -1 and 1, "categories": {"positive": percentage, "neutral": percentage, "negative": percentage}, "insights": {"positiveTrends": string, "areasForImprovement": string, "platformFocus": string}, "keywords": [{"word": string, "count": number, "sentiment": number}], "platforms": [{"name": string, "sentiment": number, "volume": number}], "timeline": [{"period": string, "sentiment": number}]}. Timeline must include recent months leading up to ${currentMonth}.`
  }, {
    role: "user",
    content: `Analyze current public sentiment for "${companyName}" regarding: "${selectedQuestion}". Include timeline data up to ${currentMonth} ${currentYear}. Only return JSON.`
  }];

  const response = await makeAIApiCall("/v1/chat/completions", messages, aiEngine === "openai" ? "gpt-5.4-nano" : "deepseek-chat", aiEngine);

  if (response.ok) {
    const data = await response.json();
    try {
      let jsonResponse;
      if (aiEngine === "openai") {
        jsonResponse = JSON.parse(data.choices[0].message.content);
      } else {
        jsonResponse = JSON.parse(data.choices[0].message.content);
      }
      return jsonResponse;
    } catch (e) {
      console.error("Failed to parse JSON response", e);
      throw new Error("Failed to parse AI response");
    }
  } else {
    console.error("API request failed", await response.text());
    throw new Error(`${aiEngine} API request failed`);
  }
};

export const getFallbackResults = (): SentimentResults => {
  const currentDate = new Date();
  const currentMonth = currentDate.toLocaleString('default', { month: 'short' });
  
  return {
    overall: 0.3,
    categories: {
      positive: 55,
      neutral: 30,
      negative: 15
    },
    insights: {
      positiveTrends: "Customers appreciate product quality and innovation",
      areasForImprovement: "Customer service response times could be improved",
      platformFocus: "Most positive engagement occurs on LinkedIn and Instagram"
    },
    keywords: [{
      word: "quality",
      count: 45,
      sentiment: 0.8
    }, {
      word: "price",
      count: 30,
      sentiment: -0.2
    }, {
      word: "support",
      count: 25,
      sentiment: 0.5
    }, {
      word: "innovation",
      count: 20,
      sentiment: 0.9
    }, {
      word: "delivery",
      count: 15,
      sentiment: -0.4
    }],
    platforms: [{
      name: "Twitter",
      sentiment: 0.2,
      volume: 45
    }, {
      name: "Facebook",
      sentiment: 0.4,
      volume: 25
    }, {
      name: "Reddit",
      sentiment: 0.1,
      volume: 15
    }, {
      name: "Instagram",
      sentiment: 0.6,
      volume: 10
    }, {
      name: "LinkedIn",
      sentiment: 0.3,
      volume: 20
    }],
    timeline: [{
      period: "Feb",
      sentiment: 0.2
    }, {
      period: "Mar",
      sentiment: 0.3
    }, {
      period: "Apr",
      sentiment: 0.1
    }, {
      period: "May",
      sentiment: 0.4
    }, {
      period: "Jun",
      sentiment: 0.5
    }, {
      period: currentMonth,
      sentiment: 0.3
    }]
  };
};

export const getSentimentColor = (sentiment: number): string => {
  if (sentiment > 0.3) return "text-green-500";
  if (sentiment < -0.3) return "text-red-500";
  return "text-yellow-500";
};

export const getSentimentText = (sentiment: number): string => {
  if (sentiment > 0.3) return "Positive";
  if (sentiment < -0.3) return "Negative";
  return "Neutral";
};
